import { GridRowSelectionModel } from "@mui/x-data-grid-premium";
import React from "react";
import { AdditionalItemInput } from "../../trees-virtual/types.js";
import {
  createRecursiveTryFocusElementFunction,
  isInTestMode,
} from "../../utils.js";

/**
 * Shared mount-level UI state for the document DataGrid components (project
 * quote + template): the multi-select model and the inline create-input
 * position. Owns the focus selector and rank math so they stay identical
 * across document types.
 */
export function useDocDataGridShell(): {
  rowSelectionModel: GridRowSelectionModel;
  setRowSelectionModel: React.Dispatch<
    React.SetStateAction<GridRowSelectionModel>
  >;
  checkedItemIds: string[];
  isItemChecked: (id: string) => boolean;
  clearChecked: () => void;
  additionalItemInput: AdditionalItemInput | null;
  setAdditionalItemInput: React.Dispatch<
    React.SetStateAction<AdditionalItemInput | null>
  >;
  onAdditionalItemInputChange: (input: AdditionalItemInput) => void;
} {
  const [rowSelectionModel, setRowSelectionModel] =
    React.useState<GridRowSelectionModel>({ type: "include", ids: new Set() });

  const checkedItemIds = React.useMemo(
    () => [...rowSelectionModel.ids].map(String),
    [rowSelectionModel.ids]
  );
  const isItemChecked = React.useCallback(
    (id: string) => rowSelectionModel.ids.has(id),
    [rowSelectionModel.ids]
  );
  const clearChecked = React.useCallback(
    () => setRowSelectionModel({ type: "include", ids: new Set() }),
    []
  );

  const [additionalItemInput, setAdditionalItemInput] =
    React.useState<AdditionalItemInput | null>(null);

  const onAdditionalItemInputChange = React.useCallback(
    (input: AdditionalItemInput) => {
      setAdditionalItemInput(input);
      setTimeout(() => {
        const inputRank =
          input.itemRank + (input.position === "below" ? 0.5 : -0.5);
        if (!isInTestMode())
          createRecursiveTryFocusElementFunction(
            `input#msys-datagrid-input-${input.itemParentType}-${input.itemParentId}[data-node-rank="${inputRank}"]:enabled`
          )();
      });
    },
    []
  );

  return {
    rowSelectionModel,
    setRowSelectionModel,
    checkedItemIds,
    isItemChecked,
    clearChecked,
    additionalItemInput,
    setAdditionalItemInput,
    onAdditionalItemInputChange,
  };
}
